import React, { useState, useEffect } from 'react';
import { 
  Box, Heading, VStack, Text, Divider, 
  Flex, Link, Spinner, useColorModeValue,
  Alert, AlertIcon
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { supabase } from '../../integrations/supabase/client';

const EntrepreneurNews = () => {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  
  // Color settings
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const color = "green.500"; // Green theme for entrepreneur news
  
  // 固定的卡片高度
  const CARD_HEIGHT = "400px";
  
  // 监听网络状态变化
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  useEffect(() => {
    const fetchEntrepreneurArticles = async () => {
      if (isOffline) {
        setIsLoading(false);
        setError("网络连接已断开");
        return;
      }
      
      setIsLoading(true);
      try {
        // 1. Get all feed_ids with category "企业家动态" from feed table
        const { data: feedsData, error: feedsError } = await supabase
          .from('feed')
          .select('feed_id')
          .eq('category', '企业家动态');

        if (feedsError) throw feedsError;

        // If no matching feeds found
        if (!feedsData || feedsData.length === 0) {
          setError('No "企业家动态" category found, please add this category to the database first');
          setArticles([]);
          setIsLoading(false);
          return;
        }
        
        // 提取feed_id列表
        const feedIds = feedsData.map(feed => feed.feed_id);
        console.log('Retrieved 企业家动态 feedIds:', feedIds);

        // 2. Use feed_id to get articles from article table
        const { data: articlesData, error: articlesError } = await supabase
          .from('article')
          .select('*')
          .in('feed_id', feedIds)
          .order('published_at', { ascending: false })
          .limit(20); // Get more articles for scrolling

        if (articlesError) throw articlesError;

        console.log(`Retrieved ${articlesData?.length || 0} 企业家动态 articles`);

        // If no articles found, show error message
        if (!articlesData || articlesData.length === 0) {
          setError('No 企业家动态 articles found, please ensure there are related articles in the database');
        }

        setArticles(articlesData || []);
      } catch (error) {
        console.error('Failed to fetch 企业家动态 articles:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEntrepreneurArticles();
  }, [isOffline]);
  
  if (isLoading) {
    return (
      <Box 
        borderWidth="1px"
        borderRadius="lg"
        p={5}
        boxShadow="sm"
        bg={bgColor}
        borderColor={borderColor}
        height={CARD_HEIGHT}
      >
        <Heading 
          size="md" 
          mb={4} 
          color={color}
          pb={2}
          borderBottom={`2px solid ${color}`}
        >
          Entrepreneur News
        </Heading>
        <Flex justify="center" align="center" height="calc(100% - 60px)">
          <Spinner color={color} size="xl" />
        </Flex>
      </Box>
    );
  }
  
  if (isOffline) {
    return (
      <Box 
        borderWidth="1px"
        borderRadius="lg"
        p={5}
        boxShadow="sm"
        bg={bgColor}
        borderColor={borderColor}
        height={CARD_HEIGHT}
      >
        <Heading 
          size="md" 
          mb={4} 
          color={color}
          pb={2}
          borderBottom={`2px solid ${color}`}
        >
          Entrepreneur News
        </Heading>
        <Flex justify="center" align="center" height="calc(100% - 60px)" direction="column">
          <Alert status="warning" mb={4}>
            <AlertIcon />
            网络连接已断开
          </Alert>
          <Text color="gray.500" fontSize="sm">
            请检查您的网络连接后重试
          </Text>
        </Flex>
      </Box>
    );
  }
  
  if (error || articles.length === 0) {
    return (
      <Box 
        borderWidth="1px"
        borderRadius="lg"
        p={5}
        boxShadow="sm"
        bg={bgColor}
        borderColor={borderColor}
        height={CARD_HEIGHT}
      >
        <Heading 
          size="md" 
          mb={4} 
          color={color}
          pb={2}
          borderBottom={`2px solid ${color}`}
        >
          Entrepreneur News
        </Heading>
        <Flex justify="center" align="center" height="calc(100% - 60px)">
          <Alert status="warning">
            <AlertIcon />
            {error || 'No Entrepreneur News articles available'}
          </Alert>
        </Flex>
      </Box>
    );
  }
  
  return (
    <Box 
      borderWidth="1px"
      borderRadius="lg"
      p={5}
      boxShadow="sm"
      bg={bgColor}
      borderColor={borderColor}
      height={CARD_HEIGHT}
      position="relative"
    >
      <Heading 
        size="md" 
        mb={4} 
        color={color}
        pb={2}
        borderBottom={`2px solid ${color}`}
      >
        Entrepreneur News
      </Heading>
      
      {/* 可滚动的内容区域 */}
      <Box 
        height="calc(100% - 80px)" 
        overflow="auto" 
        pr={2}
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'gray',
            borderRadius: '24px',
          },
        }}
      >
        <VStack spacing={3} align="stretch">
          {articles.map((article, idx) => (
            <Box key={article.id || idx}>
              {idx > 0 && <Divider my={2} />}
              <Link 
                as={RouterLink} 
                to={`/article/${article.article_id}`}
                fontWeight="medium"
                display="block"
                noOfLines={2}
                mb={1}
                _hover={{ color }}
              >
                {article.title}
              </Link>
              <Text fontSize="sm" color="gray.500" noOfLines={2}>
                {article.description}
              </Text>
            </Box>
          ))}
        </VStack>
      </Box>
      
      {/* 底部阴影渐变，提示可以滚动 */}
      {articles.length > 4 && (
        <Box 
          position="absolute" 
          bottom="0" 
          left="0" 
          right="0" 
          height="40px" 
          bgGradient={`linear(to-t, ${bgColor}, transparent)`}
          pointerEvents="none"
          borderBottomRadius="lg"
        />
      )}
      
      {/* View more link positioned above bottom shadow area */}
      <Flex
        justify="flex-end"
        position="absolute"
        bottom="5"
        right="5"
        zIndex="1"
      >
        <Link
          as={RouterLink}
          to={`/category/企业家动态`}
          color={color}
          fontSize="sm"
          fontWeight="medium"
          display="flex"
          alignItems="center"
          bg={bgColor}
          px={2}
          borderRadius="md"
        >
          View More
          <ArrowForwardIcon ml={1} />
        </Link>
      </Flex>
    </Box>
  );
};

export default EntrepreneurNews; 